/**
 * A job offer, over whatever's on screen: where and when, how far, the fare and how it's
 * paid, with a countdown. It chimes and buzzes until it's answered or runs out.
 */
import { ApiError, errorMessage, localTime, post } from "@taxi/mobile-web/api";
import { miles, minutes, money } from "@taxi/mobile-web/format";
import { useNow } from "@taxi/mobile-web/hooks";
import { NavigateIcon, UsersIcon } from "@taxi/mobile-web/icons";
import { navigate } from "@taxi/mobile-web/router";
import { Button, useToast } from "@taxi/mobile-web/ui";
import { useEffect, useState } from "react";
import { PaymentTag, pickupWhen, TripRoute } from "../components/parts";
import { startOfferAlert } from "../lib/device";
import { useDriver } from "../lib/driver";
import type { DriverJob, DriverOffer } from "../lib/types";

/** When the offer runs out, on this phone's clock (never more than its length from now). */
export function offerDeadline(offer: DriverOffer, now = Date.now()): number {
  const deadline = localTime(offer.expiresAt);
  const longest = now + offer.timeoutSeconds * 1000;
  return Number.isFinite(deadline) ? Math.min(deadline, longest) : longest;
}

export function OfferOverlay({ offer }: { offer: DriverOffer }) {
  const { dismissOffer, refresh } = useDriver();
  const toast = useToast();
  const [deadline] = useState(() => offerDeadline(offer));
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const now = useNow(250).getTime();
  const left = Math.max(0, deadline - now);
  const total = offer.timeoutSeconds * 1000;
  const expired = left <= 0;

  // The chime and buzz, until it's answered or gone.
  useEffect(() => startOfferAlert(), []);

  useEffect(() => {
    if (!expired || busy) return;
    dismissOffer(offer.id);
    toast("The offer ran out of time");
    void refresh();
  }, [expired, busy, offer.id, dismissOffer, refresh, toast]);

  async function respond(accept: boolean) {
    setBusy(accept ? "accept" : "decline");
    try {
      const job = await post<DriverJob>(
        `/driver/offers/${offer.id}/${accept ? "accept" : "decline"}`,
      );
      dismissOffer(offer.id);
      if (accept) navigate(`/jobs/${job.id}`);
      else toast("Declined. It's gone to the next driver.");
    } catch (error) {
      dismissOffer(offer.id);
      toast(
        error instanceof ApiError && error.status === 409
          ? "Too late: that offer has gone"
          : errorMessage(error),
        "error",
      );
    } finally {
      setBusy(null);
      void refresh();
    }
  }

  const seconds = Math.ceil(left / 1000);
  const fraction = Math.min(1, left / total);
  const ring = 2 * Math.PI * 30;
  return (
    <div className="offer-backdrop">
      <section className="offer" role="alertdialog" aria-modal="true" aria-label="New job offer">
        <header className="offer-head">
          <span className="offer-kicker">
            <i aria-hidden="true" /> New job · {pickupWhen(offer)}
          </span>
          <div
            className={`countdown${seconds <= 10 ? " countdown-low" : ""}`}
            role="timer"
            aria-label={`${seconds} seconds to answer`}
          >
            <svg viewBox="0 0 72 72" aria-hidden="true">
              <circle cx="36" cy="36" r="30" className="countdown-track" />
              <circle
                cx="36"
                cy="36"
                r="30"
                className="countdown-left"
                strokeDasharray={ring}
                strokeDashoffset={ring * (1 - fraction)}
              />
            </svg>
            <span>{seconds}</span>
          </div>
        </header>
        <div className="offer-fare">
          <strong>{money(offer.pricePence)}</strong>
          {offer.isEstimate && <small>estimate</small>}
          <PaymentTag method={offer.paymentMethod} />
        </div>
        {offer.driveSeconds !== null && (
          <p className="offer-away">
            <NavigateIcon size={16} />
            <strong>{minutes(offer.driveSeconds)}</strong>
            {offer.driveMetres !== null && ` · ${miles(offer.driveMetres)}`} to the pickup
          </p>
        )}
        <TripRoute pickup={offer.pickup} dropoff={offer.dropoff} />
        <div className="offer-facts">
          <span>
            <UsersIcon size={16} /> {offer.passengers}{" "}
            {offer.passengers === 1 ? "passenger" : "passengers"}
          </span>
          <span>{offer.vehicleTypeName}</span>
        </div>
        <Button
          className="offer-accept"
          loading={busy === "accept"}
          disabled={busy !== null}
          onClick={() => respond(true)}
        >
          <span
            className="offer-timer"
            style={{ transform: `scaleX(${fraction})` }}
            aria-hidden="true"
          />
          <span className="offer-accept-label">Accept</span>
        </Button>
        <Button
          variant="ghost"
          loading={busy === "decline"}
          disabled={busy !== null}
          onClick={() => respond(false)}
        >
          Decline
        </Button>
      </section>
    </div>
  );
}
